import React, { useEffect } from "react";
import LegalPage from "./LegalPage";
import useNoIndex from "./useNoIndex";

const toRoman = (value) => {
  const numerals = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let remaining = value;

  return numerals.reduce((result, [amount, numeral]) => {
    while (remaining >= amount) {
      result += numeral;
      remaining -= amount;
    }
    return result;
  }, "");
};

const Section = ({ title, children, id }) => {
  const titleParts = title.match(/^(\d+)\.\s*(.+)$/);
  const sectionNumber = titleParts ? Number(titleParts[1]) : null;
  const sectionTitle = titleParts ? titleParts[2] : title;

  return (
    <section id={id}>
      <h2 className="mb-3 text-xl font-bold text-[#172033]">
        {sectionNumber ? `Section ${toRoman(sectionNumber)}: ` : ""}{sectionTitle}
      </h2>
      <div className="space-y-3 text-[#3A4658]">
        {React.Children.toArray(children).map((child, index) => (
          <div key={index} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-1.5">
            <span className="font-semibold text-[#172033]">{toRoman(index + 1).toLowerCase()}.</span>
            <div className="min-w-0">{child}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const TermsAndConditions = () => {
  useNoIndex();

  useEffect(() => {
    document.title = "Terms and Conditions | CoursePivot";
  }, []);

  return (
    <LegalPage title="Terms and Conditions" effectiveDate="22nd September 2026">
      <Section title="1. Agreement and Scope">
        <p>
          Welcome to CoursePivot.com and the CoursePivot customer dashboard.
          These Terms of Service govern your use of our website, order
          dashboard, writing services, support tools, and related applications.
          By using our services, you agree to these terms.
        </p>
        <p>
          If you do not agree with these terms, you should not access or use the
          services. For questions about these terms, contact us at
          admin@coursepivot.com.
        </p>
      </Section>

      <Section title="2. Updates to These Terms">
        <p>
          We may update these terms as our services improve or legal,
          operational, or payment requirements change. When updates are made, we
          may post the revised terms on this page or notify users through the
          dashboard or email.
        </p>
        <p>
          Continued use of CoursePivot after an update means you accept the
          revised terms.
        </p>
      </Section>

      <Section title="3. Privacy and Age Requirements">
        <p>
          Your privacy matters to us. Please review our Privacy Policy to
          understand how we collect, use, and protect information connected to
          your account and orders.
        </p>
        <p>
          CoursePivot does not knowingly collect personal information from
          children under 13. If we learn that such information has been
          collected, we will delete it promptly.
        </p>
      </Section>

      <Section title="4. Account Registration and Security">
        <p>
          Some services require an account. You agree to provide accurate,
          complete, and current information when registering or placing an
          order. You are responsible for protecting your password and account
          access.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Do not impersonate another person or use false details.</li>
          <li>Do not transfer your account without permission.</li>
          <li>Notify us if you believe your account has been compromised.</li>
        </ul>
      </Section>

      <Section title="5. Academic Use and Client Responsibility">
        <p>
          CoursePivot provides academic support, model papers, drafting,
          editing, research assistance, tutoring-style guidance, and related
          writing services. Delivered work is intended to be used responsibly as
          reference, study support, or a model for your own learning.
        </p>
        <p>
          You are responsible for following your school, university, employer,
          or institution's academic integrity rules. You should not submit any
          work in a way that violates rules that apply to you.
        </p>
      </Section>

      <Section title="6. Orders, Instructions, and Deadlines">
        <p>
          You are responsible for providing accurate and complete order details,
          including instructions, files, rubrics, deadline, citation style, page
          count, course materials, and any special requirements before payment
          is completed.
        </p>
        <p>
          Missing, unclear, inaccurate, or late instructions may affect the
          price, delivery timeline, revision eligibility, or refund eligibility.
          New requirements submitted after work begins may require an additional
          payment or a deadline extension.
        </p>
        <p>
          An order is considered active only after successful payment, unless
          CoursePivot has expressly agreed otherwise. Draft or pending payment
          orders may not be assigned to a writer until payment is completed.
        </p>
      </Section>

      <Section title="7. Delivery, Review, and Acceptance">
        <p>
          Completed essays, papers, and assignments are delivered through your
          CoursePivot dashboard. You are responsible for reviewing the delivered
          file before using or submitting it.
        </p>
        <p>
          Delivery may include files, messages, final submissions, revision
          files, or other materials made available in your dashboard or order
          thread. Once you accept a final delivery in the dashboard, submit the
          work for grading, or otherwise use the delivered work, refund and free
          revision options may be limited.
        </p>
      </Section>

      <Section title="8. Revision Policy" id="revision-policy">
        <p>
          Revision requests must be made within 7 days of delivery. Free
          revisions must be based on the original instructions submitted with
          the order and must clearly identify what should be reviewed or
          corrected.
        </p>
        <p>
          Free revisions do not cover new instructions, changed topics, added
          sources, additional pages, changed rubrics, changed citation style, or
          major changes that were not part of the original order. Those requests
          may require an additional fee or a new deadline.
        </p>
      </Section>

      <Section title="9. Cancellation Policy">
        <p>
          You may cancel a draft or pending payment order before payment without
          charge. Once payment is completed, cancellation eligibility depends on
          the status and progress of the order.
        </p>
        <p>
          A cancellation request must be submitted before any delivery is made,
          whether the delivery is late or not. Once an order has been delivered,
          the order is no longer treated as a cancellation request; any refund
          is reviewed by the admin, who determines the refundable amount based
          on the work delivered and the order circumstances, up to a maximum of
          50% of the total order amount.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Before writer assignment or work begins, cancellation may qualify
            for a full refund or wallet credit.
          </li>
          <li>
            After writer assignment but before substantial work begins,
            cancellation may qualify for a partial refund, less payment,
            administrative, or preparation costs.
          </li>
          <li>
            After work has started, cancellation refunds are reviewed based on
            completed work, time spent, deadline urgency, and resources already
            committed.
          </li>
          <li>
            After delivery, the order is handled under the revision, complaint,
            and admin refund review process rather than cancellation, with any
            approved refund capped at 50% of the total order amount.
          </li>
        </ul>
        <p>
          Cancellation may be limited for urgent orders, orders near the
          deadline, orders delayed by missing client information, or orders
          where a writer has already completed substantial work.
        </p>
      </Section>

      <Section title="10. Writer Protection and Reassignment Compensation">
        <p>
          CoursePivot may review order history, instructions, messages,
          delivered files, revision requests, writer responses, and related
          records when a client asks to cancel, restart, or place a
          substantially similar order after a writer has already completed
          meaningful work in good faith.
        </p>
        <p>
          If CoursePivot determines that the previous writer completed
          substantial work, remained reasonably willing to revise within the
          original order scope, and the client is seeking to remove that writer
          for reasons other than verified quality, conduct, or performance
          issues, CoursePivot may require a reassignment compensation charge
          before assigning the same or substantially similar work to a new
          writer.
        </p>
        <p>
          This charge may be up to 25% of the current or replacement order
          value and is intended to compensate the previous writer for work
          already performed. The charge is not automatic and applies only
          after admin review.
        </p>
        <p>
          A client may not avoid this policy by cancelling an order and placing
          the same or substantially similar order again under a new order ID,
          revised wording, or a different writer request.
        </p>
        <p>
          This policy does not apply where cancellation, reassignment, or a
          refund is caused by clear writer non-performance, missed delivery
          without reasonable cause, refusal to revise within the original
          order scope, verified plagiarism, prohibited AI use, or other
          substantiated writer quality or conduct issues.
        </p>
      </Section>

      <Section title="11. Refund Policy">
        <p>
          Refunds may be considered within 14 days after order delivery for
          significant quality issues that are clearly documented and submitted
          for review. CoursePivot may offer a revision, partial refund, full
          refund, wallet credit, or another reasonable resolution depending on
          the situation.
        </p>
        <p>Refunds may be considered for:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Duplicate payments or incorrect charges.</li>
          <li>Non-delivery of an active paid order.</li>
          <li>
            Significant failure to follow original instructions after a
            reasonable revision opportunity.
          </li>
          <li>
            Verified plagiarism, AI-content, or quality concerns supported by
            credible evidence.
          </li>
          <li>
            Company-caused deadline issues that materially affect the usefulness
            of the delivered work.
          </li>
        </ul>
        <p>Refunds are generally not available when:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>The work has been accepted in the dashboard.</li>
          <li>The request is based on new or changed instructions.</li>
          <li>
            The issue was caused by missing, late, unclear, or inaccurate client
            instructions.
          </li>
          <li>The client missed the revision, complaint, or refund window.</li>
          <li>
            The client refuses a reasonable revision process before requesting a
            refund.
          </li>
          <li>
            The complaint is based only on a grade or outcome without specific
            evidence of a service issue.
          </li>
        </ul>
        <p>
          Approved refunds are normally returned to the original payment method
          where possible. If an original payment-method refund is not available,
          CoursePivot may issue wallet credit or another reasonable resolution.
        </p>
      </Section>

      <Section title="12. Complaints and Dispute Review">
        <p>
          Complaints should be submitted through the dashboard or by contacting
          support. Include the order ID, a clear description of the issue, your
          preferred resolution, and any supporting documents or reports.
        </p>
        <p>
          CoursePivot may review order instructions, messages, uploaded files,
          drafts, final delivery, revision history, payment records, and
          supporting evidence before deciding on a resolution.
        </p>
        <p>
          Possible outcomes include revision, explanation, partial refund, full
          refund, wallet credit, additional service, or denial of the complaint
          where the issue is not supported by the order record.
        </p>
      </Section>

      <Section title="13. Payments, Wallets, and Extra Payments">
        <p>
          Payments are processed through secure payment providers. Wallet
          balances, top-ups, extra payments, and order payments are reflected in
          your dashboard after successful processing.
        </p>
        <p>
          You are responsible for ensuring that your order details, deadline,
          files, wallet balance, payment method, and payment information are
          accurate before submitting payment.
        </p>
        <p>
          Wallet balances may be used for eligible order payments and extra
          payments. Paid wallet deposits may be refundable where technically and
          commercially possible. Promotional credits, goodwill credits, discounts,
          and bonuses are not redeemable for cash unless required by law.
        </p>
        <p>
          Extra payments may be used for added services, upgraded requirements,
          order changes, writer support, or other order-related charges. Extra
          payments are refundable only when the related service or order benefit
          was not provided.
        </p>
      </Section>

      <Section title="14. Referral Program Terms" id="referral-program-terms">
        <p>
          CoursePivot may offer a referral program to eligible clients. Each
          client may receive a personal referral link or code that can be shared
          with new clients. A referral is not approved at signup alone.
        </p>
        <p>
          A referral qualifies only when a new client signs up through a valid
          referral link or code, places their first qualifying paid order of at
          least $30, and that order is completed, accepted, and not cancelled,
          refunded, reversed, charged back, or disputed.
        </p>
        <p>
          For each approved qualifying referral, the referring client earns $40
          in referral credit. Approved referral credit is added to the available
          wallet balance and may be used on future eligible orders. If referral
          credit is spent from the wallet, the amount spent will no longer be
          available for payout.
        </p>
        <p>
          Referred clients may receive the current new-client or sitewide
          promotion shown during signup or checkout. Discounts and promotional
          offers do not stack unless CoursePivot states otherwise; where more
          than one promotion could apply, CoursePivot may apply the best
          available eligible discount.
        </p>
        <p>
          Referrals may be reviewed, held, rejected, or discredited where we
          identify self-referrals, duplicate or related accounts, existing
          customer identities, matching or related email addresses, phone
          numbers, names, billing or payment details where available, device,
          browser, IP, or other abuse signals, fake information, coordinated
          misuse, or any attempt to manipulate the program.
        </p>
        <p>
          Referral credit may be disapproved or removed if the referred order is
          cancelled, refunded, disputed, reversed, charged back, below the
          minimum qualifying amount, connected to policy violations, or later
          found to be ineligible. CoursePivot does not have to disclose every
          internal fraud or risk signal used in a review.
        </p>
        <p>
          Payout requests may be submitted only when available unused referral
          credit reaches at least $50. To request payout, the client must
          provide a valid PayPal email address and the full name on the PayPal
          account. Payouts are reviewed and processed manually through PayPal,
          and only eligible unused approved referral credit can be paid out.
        </p>
        <p>
          A payout request may be rejected or delayed if the available referral
          credit is below the minimum, the PayPal information is missing or
          inaccurate, a duplicate payout request is already pending, any related
          referral is under review, the referred order has an unresolved refund,
          cancellation, dispute, chargeback, or reversal, or CoursePivot cannot
          reasonably verify that the rewards are eligible.
        </p>
        <p>
          CoursePivot may update, pause, limit, or end the referral program at
          any time. Changes will not affect referral credit already approved and
          available in a client wallet unless the credit was approved in error,
          connected to misuse, or later becomes ineligible under these Terms.
        </p>
      </Section>

      <Section title="15. Loyalty Program Terms" id="loyalty-program-terms">
        <p>
          The loyalty program begins on its published launch date. Earlier
          orders do not count. Eligible customers begin with the standard 30%
          discount and may unlock higher discounts through either completed
          qualifying orders or qualifying spending.
        </p>
        <p>
          Only paid orders completed after launch qualify. Drafts, unpaid,
          cancelled, refunded, reversed, or charged-back orders, tips, wallet
          top-ups, referral rewards, and administrative wallet adjustments do
          not count. Adjustments may reduce loyalty progress where appropriate.
        </p>
        <p>
          A newly unlocked level applies to future eligible orders and does not
          change earlier order prices. Referral first-order offers do not stack
          with loyalty discounts. The applicable discount and program version
          are saved with each order.
        </p>
        <p>
          After six months without a completed qualifying order, an earned
          level is reduced by one step. It may reduce by one further step after
          every additional three months of inactivity until it reaches the
          standard 30% level. A completed qualifying order resets the inactivity
          period.
        </p>
        <p>
          CoursePivot may update future targets, percentages, eligibility rules,
          or availability of the program. Changes do not alter discounts already
          saved on paid orders.
        </p>
      </Section>

      <Section title="16. Chargebacks and Payment Reversals">
        <p>
          If you believe there is a payment or service problem, contact
          CoursePivot support or use the dashboard complaint process first so we
          can review and resolve the issue.
        </p>
        <p>
          If a PayPal, bank, card, or payment-provider dispute is opened,
          CoursePivot may provide order records, delivery proof, dashboard
          activity, messages, uploaded files, and support history to the payment
          provider as evidence. Payment-provider decisions may affect account
          access, wallet balances, order access, and available resolutions.
        </p>
      </Section>

      <Section title="17. Plagiarism and AI Content Checks">
        <p>
          CoursePivot uses Turnitin as a primary tool for plagiarism and
          AI-content review. If you want us to consider another detector, you
          must specify that when placing the order and provide access if needed.
        </p>
        <p>
          Similarity or AI disputes must be supported by a valid report and
          submitted within the revision or refund review period.
        </p>
        <p>
          Free, anonymous, incomplete, or unverifiable detector results may not
          be conclusive by themselves. CoursePivot may review the file,
          instructions, sources, report details, and available tools before
          deciding on a revision, refund, or other resolution.
        </p>
      </Section>

      <Section title="18. Prohibited Uses">
        <p>
          You agree not to misuse CoursePivot or request services for unlawful,
          fraudulent, harmful, abusive, or deceptive purposes. You must not
          upload malware, impersonate others, interfere with the dashboard,
          attempt unauthorized access, or use our services in a way that
          violates applicable law or another person's rights.
        </p>
      </Section>

      <Section title="19. Disputes, Liability, and Responsibility">
        <p>
          You agree to use our services lawfully and responsibly. You also agree
          to indemnify CoursePivot, its team, and affiliates from claims arising
          from misuse of the services, violation of these terms, or inaccurate
          information supplied by you.
        </p>
        <p>
          To the fullest extent permitted by law, CoursePivot is not responsible
          for indirect, incidental, consequential, academic, institutional,
          grading, disciplinary, or third-party losses arising from your use or
          misuse of the services.
        </p>
      </Section>

      <Section title="20. Miscellaneous">
        <p>
          These terms form the agreement between you and CoursePivot for use of
          our services. If any part of these terms is found invalid, the
          remaining terms continue to apply.
        </p>
      </Section>

      <Section title="21. Ongoing Support Requests">
        <p>
          Verify your account email before submitting a new course, class, program,
          or long-term support request. One open request awaiting writer assignment
          is allowed per account across CoursePivot and Genius Professors.
          Assignment of a writer counts as approval to submit your next request;
          an administrator cancelling the pending request also removes that block.
          Quotes and messages alone do not constitute this approval.
        </p>
        <p>
          If you already have multiple unassigned requests, each open request must
          be assigned or cancelled before you submit another. You can continue
          using existing conversations, files, and payments while you wait.
        </p>
      </Section>

      <Section title="22. Contact">
        <p>
          For questions, concerns, cancellation requests, refund requests, or
          complaint support, contact admin@coursepivot.com or use the support
          options available in your dashboard.
        </p>
      </Section>
    </LegalPage>
  );
};

export default TermsAndConditions;
